import React, { JSX, useEffect, useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { ProviderBrandIcon, YOUTUBE_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { SESSION_REFUSAL_COPY, YouTubeTermsFooter } from 'components/YouTubeSessionWidget'
import { graphqlRequest } from 'lib/helpers'
import { httpsImageOrNull, YouTubeSessionsStore } from 'hooks/useYouTubeSessions'
import {
  EventDetail,
  EventYouTubeForeignSource,
  EventYouTubeForeignSourceResult,
  YouTubeChatSession
} from 'types/api'
import attachForeignSourceMutation from 'graphql/attachEventYouTubeForeignSource.graphql'
import detachForeignSourceMutation from 'graphql/detachEventYouTubeForeignSource.graphql'
import stopYouTubeChatSessionMutation from 'graphql/stopYouTubeChatSession.graphql'

export const eventHasYouTubeSource = (
  event: Pick<EventDetail, 'youtubeSourceAttached' | 'youtubeForeignSources'>
): boolean =>
  event.youtubeSourceAttached === true || (event.youtubeForeignSources?.length ?? 0) > 0

export const TWITCH_CHANNELS_OPTIONAL_HINT =
  'Twitch channels are optional when Count YouTube votes is on and the stream has a YouTube source.'

export const YOUTUBE_LINK_HELP =
  'Paste the stream\'s Share link (youtube.com/live/...) or its watch?v= link. A channel page link will not work.'

export const FOREIGN_ATTESTATION_LABEL = 'I have the channel owner\'s permission'

const watchLinkOf = (videoId: string): string =>
  `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`

const upsertSource = (
  list: EventYouTubeForeignSource[],
  source: EventYouTubeForeignSource
): EventYouTubeForeignSource[] =>
  list.some((s) => s.channelId === source.channelId)
    ? list.map((s) => (s.channelId === source.channelId ? source : s))
    : [...list, source]

const permissionDateOf = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

export const LiveDot = ({ testId }: { testId?: string }): JSX.Element => (
  <span
    data-testid={testId}
    className='inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400'
    title='Live, reading YouTube chat'
  >
    <span className='size-1.5 rounded-full bg-emerald-400' aria-hidden='true' />
    Live
  </span>
)

const RefusalBanner = ({ code, message }: { code: string | null, message: string | null }): JSX.Element => (
  <div
    data-testid='foreign-source-refusal'
    className='mt-2 flex items-start gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-xs text-dm-text-secondary'
  >
    <InformationCircleIcon className='mt-0.5 size-4 shrink-0 text-brand' aria-hidden='true' />
    <span>
      {(code !== null ? SESSION_REFUSAL_COPY[code] : undefined) ?? message ?? 'That YouTube chat could not be added. Try again in a moment.'}
    </span>
  </div>
)

const AttachForm = ({ url, onUrl, attested, onAttested, busy, submitLabel, onSubmit, onCancel, testIdPrefix }: {
  url: string
  onUrl: (v: string) => void
  attested: boolean
  onAttested: (v: boolean) => void
  busy: boolean
  submitLabel: string
  onSubmit: () => void
  onCancel?: () => void
  testIdPrefix: string
}): JSX.Element => (
  <div className='space-y-1.5'>
    <div className='flex items-center gap-2'>
      <input
        value={url}
        onChange={(e) => onUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            if (!busy && attested && url.trim() !== '') onSubmit()
          }
        }}
        placeholder='https://www.youtube.com/live/...'
        data-testid={`${testIdPrefix}-url`}
        className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-1.5 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
      />
      <button
        type='button'
        onClick={onSubmit}
        disabled={busy || !attested || url.trim() === ''}
        data-testid={`${testIdPrefix}-submit`}
        className='inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
      >
        {busy && <LoadingSpinner />}
        {submitLabel}
      </button>
      {onCancel !== undefined && (
        <button
          type='button'
          onClick={onCancel}
          disabled={busy}
          className='shrink-0 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
        >
          Cancel
        </button>
      )}
    </div>
    <p className='text-[11px] text-dm-text-accent' data-testid={`${testIdPrefix}-help`}>{YOUTUBE_LINK_HELP}</p>
    <label className='flex items-center gap-2 text-xs text-dm-text-primary'>
      <input
        type='checkbox'
        checked={attested}
        onChange={(e) => onAttested(e.target.checked)}
        data-testid={`${testIdPrefix}-attest`}
        className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
      />
      {FOREIGN_ATTESTATION_LABEL}
    </label>
  </div>
)

export const EventYouTubeSources = ({
  event,
  store,
  superuser,
  canManage,
  onForeignSourcesChange
}: {
  event: EventDetail
  store: YouTubeSessionsStore
  superuser: boolean
  canManage: boolean
  onForeignSourcesChange?: (sources: EventYouTubeForeignSource[]) => void
}): JSX.Element | null => {
  const { auth0Token } = useAuth()
  const [sources, setSources] = useState<EventYouTubeForeignSource[]>(() => event.youtubeForeignSources ?? [])
  useEffect(() => {
    setSources(event.youtubeForeignSources ?? [])
  }, [event.youtubeForeignSources])
  const [addUrl, setAddUrl] = useState('')
  const [addAttested, setAddAttested] = useState(false)
  const [startPrompt, setStartPrompt] = useState<{ channelId: string, url: string, attested: boolean } | null>(null)
  const [busy, setBusy] = useState<{ kind: 'add' } | { kind: 'start' | 'stop' | 'remove', channelId: string } | null>(null)
  const [refusal, setRefusal] = useState<{ code: string | null, message: string | null } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const ended = event.status === 'ENDED'
  const showAdd = superuser && !ended

  const commit = (next: EventYouTubeForeignSource[]): void => {
    setSources(next)
    onForeignSourcesChange?.(next)
  }

  const sessionFor = (channelId: string): YouTubeChatSession | null =>
    store.sessions.find((s) => s.channelId === channelId && s.eventId === event.eventId) ?? null

  const attach = async (videoUrl: string, kind: 'add' | 'start', channelId: string | null): Promise<boolean> => {
    if (auth0Token === undefined || busy !== null) return false
    setBusy(kind === 'add' ? { kind: 'add' } : { kind: 'start', channelId: channelId ?? '' })
    setRefusal(null)
    setError(null)
    try {
      const { result } = await graphqlRequest<{ result: EventYouTubeForeignSourceResult | null }>(
        attachForeignSourceMutation,
        { eventId: event.eventId, videoUrl: videoUrl.trim(), attested: true },
        auth0Token
      )
      if (result == null) {
        setError('That YouTube chat could not be added. Try again in a moment.')
        return false
      }
      if (result.session != null) store.upsert(result.session)
      if (result.attached && result.source != null) {
        const source = result.source
        store.rememberMeta(source.channelId, {
          title: source.channelTitle ?? null,
          imageUrl: source.channelProfileImageUrl ?? null
        })
        commit(upsertSource(sources, source))
        return true
      }
      setRefusal({ code: result.refusalCode, message: result.message })
      return false
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That YouTube chat could not be added.')
      return false
    } finally {
      setBusy(null)
    }
  }

  const handleAdd = async (): Promise<void> => {
    if (!addAttested || addUrl.trim() === '') return
    const ok = await attach(addUrl, 'add', null)
    if (ok) {
      setAddUrl('')
      setAddAttested(false)
    }
  }

  const handleStart = async (): Promise<void> => {
    if (startPrompt === null || !startPrompt.attested || startPrompt.url.trim() === '') return
    const ok = await attach(startPrompt.url, 'start', startPrompt.channelId)
    if (ok) setStartPrompt(null)
  }

  const handleStop = async (channelId: string): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    setBusy({ kind: 'stop', channelId })
    setRefusal(null)
    setError(null)
    try {
      const { session } = await graphqlRequest<{ session: YouTubeChatSession | null }>(
        stopYouTubeChatSessionMutation, { eventId: event.eventId, channelId }, auth0Token
      )
      if (session != null) store.upsert(session)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That YouTube chat could not be stopped.')
    } finally {
      setBusy(null)
    }
  }

  const handleRemove = async (channelId: string): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    if (!globalThis.confirm('Remove this YouTube chat from the stream? Reading it stops now, and its votes stop counting.')) return
    setBusy({ kind: 'remove', channelId })
    setRefusal(null)
    setError(null)
    try {
      const { result } = await graphqlRequest<{ result: EventYouTubeForeignSourceResult | null }>(
        detachForeignSourceMutation, { eventId: event.eventId, channelId }, auth0Token
      )
      if (result == null) {
        setError('That YouTube chat could not be removed. Try again in a moment.')
        return
      }
      if (result.session != null) store.upsert(result.session)
      commit(sources.filter((s) => s.channelId !== channelId))
      if (startPrompt?.channelId === channelId) setStartPrompt(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That YouTube chat could not be removed.')
    } finally {
      setBusy(null)
    }
  }

  if (sources.length === 0 && !showAdd) return null

  return (
    <div className='mt-2 space-y-2' data-testid='event-youtube-sources'>
      {sources.length > 0 && (
        <ul className='space-y-2'>
          {sources.map((src) => {
            const session = sessionFor(src.channelId)
            const live = session?.status === 'RUNNING'
            const meta = store.meta[src.channelId]
            const picture = httpsImageOrNull(src.channelProfileImageUrl) ?? meta?.imageUrl ?? httpsImageOrNull(session?.channelProfileImageUrl)
            const title = src.channelTitle ?? meta?.title ?? session?.channelTitle ?? src.channelId
            const rowBusy = busy !== null && busy.kind !== 'add' && busy.channelId === src.channelId
            const permissionDate = permissionDateOf(src.attestedAt)
            return (
              <li
                key={src.channelId}
                data-testid={`foreign-source-${src.channelId}`}
                className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2'
              >
                <div className='flex items-center gap-2.5'>
                  <ProviderBrandIcon src={YOUTUBE_BRAND_ICON_SRC} size='sm' />
                  {picture != null && (
                    <img
                      src={picture}
                      alt=''
                      data-testid='foreign-source-picture'
                      referrerPolicy='no-referrer'
                      className='size-5 shrink-0 rounded object-cover'
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  )}
                  <span className='min-w-0 flex-1 truncate text-sm text-dm-text-primary' title={title}>{title}</span>
                  {live && <LiveDot testId='foreign-source-live' />}
                  <span className='shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300 ring-1 ring-inset ring-amber-500/30'>
                    Staff add
                  </span>
                  {superuser && !ended && !live && event.status === 'ACTIVE' && (
                    <button
                      type='button'
                      onClick={() => setStartPrompt({ channelId: src.channelId, url: watchLinkOf(src.videoId), attested: false })}
                      disabled={busy !== null}
                      data-testid='foreign-source-start'
                      className='shrink-0 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      Start
                    </button>
                  )}
                  {canManage && live && (
                    <button
                      type='button'
                      onClick={() => { void handleStop(src.channelId) }}
                      disabled={busy !== null}
                      data-testid='foreign-source-stop'
                      className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {rowBusy && busy?.kind === 'stop' && <LoadingSpinner />}
                      Stop
                    </button>
                  )}
                  {canManage && (
                    <button
                      type='button'
                      onClick={() => { void handleRemove(src.channelId) }}
                      disabled={busy !== null}
                      data-testid='foreign-source-remove'
                      className='inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {rowBusy && busy?.kind === 'remove' && <LoadingSpinner />}
                      Remove
                    </button>
                  )}
                </div>
                {permissionDate !== '' && (
                  <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='foreign-source-permission'>
                    Permission confirmed {permissionDate}
                  </p>
                )}
                {startPrompt !== null && startPrompt.channelId === src.channelId && superuser && (
                  <div className='mt-2'>
                    <AttachForm
                      url={startPrompt.url}
                      onUrl={(v) => setStartPrompt((p) => (p === null ? p : { ...p, url: v }))}
                      attested={startPrompt.attested}
                      onAttested={(v) => setStartPrompt((p) => (p === null ? p : { ...p, attested: v }))}
                      busy={rowBusy}
                      submitLabel='Start'
                      onSubmit={() => { void handleStart() }}
                      onCancel={() => setStartPrompt(null)}
                      testIdPrefix='foreign-start'
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {showAdd && (
        <div className='rounded-lg border border-dashed border-white/10 px-3 py-2.5' data-testid='foreign-source-add'>
          <div className='mb-1.5 flex items-center gap-2'>
            <ProviderBrandIcon src={YOUTUBE_BRAND_ICON_SRC} size='sm' />
            <span className='text-xs font-medium text-dm-text-primary'>Add a YouTube live chat (staff)</span>
          </div>
          <AttachForm
            url={addUrl}
            onUrl={setAddUrl}
            attested={addAttested}
            onAttested={setAddAttested}
            busy={busy?.kind === 'add'}
            submitLabel='Add'
            onSubmit={() => { void handleAdd() }}
            testIdPrefix='foreign-add'
          />
          <YouTubeTermsFooter />
        </div>
      )}
      {refusal !== null && <RefusalBanner code={refusal.code} message={refusal.message} />}
      {error !== null && (
        <div className='mt-2'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
    </div>
  )
}
